import { CSSResultGroup, html, LitElement, nothing, type TemplateResult } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { PDP_PRODUCT_API, type PdpProductAPI } from 'components/discovery/pdp/pdp-product/pdp-product-api.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import 'components/discovery/product/stock-indication/stock-indication.js'
import 'components/shared/product-preview/product-preview.js'
import 'components/shared/layout/table.js'
import 'components/discovery/plp/filters/active-filters/active-filters.js'
import 'components/discovery/pdp/provider.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import 'components/ordering/order-form/components/order-form.js'
import 'components/discovery/product/add-to-order-templates/components/order-template-slide-in/order-template-slide-in.js'
import {
  FilterRemovedEvent,
  PlpFiltersSelectedFiltersEvent,
} from 'components/discovery/plp/filters/plp-filters/events.js'
import {
  VariantMatrixColumnsAppliedEvent,
  VariantMatrixFiltersApplyEvent,
} from './variant-matrix-events.js'
import 'components/discovery/pdp/variant-matrix/slide-ins/variant-matrix-filter-slide-in.js'
import 'components/discovery/pdp/variant-matrix/slide-ins/variant-matrix-column-slide-in.js'
import { VARIANT_MATRIX_API, type VariantMatrixAPI } from './variant-matrix-api.js'
import { VariantMatrixService } from './variant-matrix.service.js'
import { variantMatrixStyle } from './variant-matrix.style.js'
import {
  createStockSelectedFilter,
  isStockSelectedFilter,
} from './variant-matrix-stock-filter.js'
import type { CSCVariantMatrixColumnSlideIn } from './slide-ins/variant-matrix-column-slide-in.js'
import type { CSCVariantMatrixFilterSlideIn } from './slide-ins/variant-matrix-filter-slide-in.js'
import type {
  VariantMatrixColumnOption,
  VariantMatrixFilter,
  VariantMatrixFilterOption,
  VariantMatrixPaginator,
  VariantMatrixProduct,
  VariantMatrixSelectedFilter,
  VariantMatrixSortMode,
  VariantMatrixServiceContract,
} from './typing/variant-matrix-typing.js'

type VariantMatrixTableColumn = {
  name: string
  key: string
  width?: string
  align?: string
  truncate?: boolean
  cellRenderer?: (value: unknown, row: VariantMatrixTableRow) => TemplateResult | typeof nothing | string
}

type VariantMatrixTableRow = {
  product: VariantMatrixProduct
  price: VariantMatrixProduct
  order: VariantMatrixProduct
  [key: `filter_${number}`]: string
}

const translations = {
  allVariants: () => t('all_variants', { ns: 'csc-variant-matrix', default: 'All variants' }),
  product: () => t('product', { ns: 'csc-variant-matrix', default: 'Product' }),
  price: () => t('price', { ns: 'csc-variant-matrix', default: 'Price' }),
} as const

initializeIconsByRole({
  'cse-chip.dropdown': { name: 'angle-down', style: 'solid' },
  'cse-chip.dismiss': { name: 'xmark', style: 'solid' },
})

export const variantMatrixHiddenFields = ['prices', 'stock', 'uom'] as const
export type VariantMatrixHiddenField = typeof variantMatrixHiddenFields[number]

@customElement('csc-variant-matrix')
export class CSCVariantMatrix extends HiddenFieldsMixin(LitElement, variantMatrixHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, variantMatrixStyle]

  @inject(PDP_PRODUCT_API)
  private productApi!: PdpProductAPI

  @inject(VARIANT_MATRIX_API)
  private matrixApi!: VariantMatrixAPI

  @inject(PDP_PRODUCT_STORE)
  private pdpProductStore!: PdpProductStore

  private pdpProductResolved = false

  @state()
  private pdpProductWithoutVariants = false

  @property({ type: Number, attribute: 'variant-group-id' })
  variantGroupId!: number

  /** @deprecated Use `variant-group-id` instead. */
  @property({ type: Number })
  set variant(value: number) {
    console.warn('<csc-variant-matrix> "variant" is deprecated, use "variant-group-id" instead.')
    this.variantGroupId = value
  }

  get variant() {
    return this.variantGroupId
  }

  @property()
  locale = 'en-US'

  @property({
    type: Array,
    attribute: 'filter-attributes',
    converter: {
      fromAttribute(value: string | null): number[] {
        return value?.split(',').map(Number).filter(n => !Number.isNaN(n)) ?? []
      },
    },
  })
  filterAttributes: number[] = []

  @state()
  private legacyDisplayedFields: Set<VariantMatrixHiddenField> | null = null

  /** @deprecated Prices are shown by default; use `hidden-fields="prices"` to hide them. */
  @property({ type: Boolean, attribute: 'display-prices' })
  set displayPrices(value: boolean) {
    this.setLegacyDisplayed('prices', 'display-prices', value)
  }

  get displayPrices() {
    return !this.isPartHidden('prices')
  }

  /** @deprecated Stock is shown by default; use `hidden-fields="stock"` to hide it. */
  @property({ type: Boolean, attribute: 'display-stock' })
  set displayStock(value: boolean) {
    this.setLegacyDisplayed('stock', 'display-stock', value)
  }

  get displayStock() {
    return !this.isPartHidden('stock')
  }

  /** @deprecated The unit of measure is shown by default; use `hidden-fields="uom"` to hide it. */
  @property({ type: Boolean, attribute: 'display-uom' })
  set displayUom(value: boolean) {
    this.setLegacyDisplayed('uom', 'display-uom', value)
  }

  get displayUom() {
    return !this.isPartHidden('uom')
  }

  private setLegacyDisplayed(field: VariantMatrixHiddenField, attribute: string, value: boolean) {
    console.warn(`<csc-variant-matrix> "${attribute}" is deprecated, it is shown by default; use hidden-fields="${field}" to hide it.`)
    const displayed = new Set(this.legacyDisplayedFields ?? [])
    if (value) displayed.add(field)
    else displayed.delete(field)
    this.legacyDisplayedFields = displayed
  }

  private isPartHidden(field: VariantMatrixHiddenField): boolean {
    if (this.isHidden(field)) return true
    return this.legacyDisplayedFields !== null && !this.legacyDisplayedFields.has(field)
  }

  @property({ type: Boolean, attribute: 'can-order' })
  canOrder = false

  @property({ type: Boolean, attribute: 'can-add-to-order-template' })
  canAddToOrderTemplate = false

  /** @deprecated Use `can-add-to-order-template` instead. */
  @property({ type: Boolean, attribute: 'order-templates-enabled' })
  set orderTemplatesEnabled(value: boolean) {
    console.warn('<csc-variant-matrix> "order-templates-enabled" is deprecated, use "can-add-to-order-template" instead.')
    this.canAddToOrderTemplate = value
  }

  get orderTemplatesEnabled() {
    return this.canAddToOrderTemplate
  }

  private deprecatedStockAmountWarningAt: number | undefined

  /** @deprecated Configure the stock indication per shop; the search response returns it. Only used for products without an indication. */
  @property({ type: Number, attribute: 'stock-amount-warning-at' })
  set stockAmountWarningAt(value: number | undefined) {
    console.warn('<csc-variant-matrix> "stock-amount-warning-at" is deprecated, configure the stock indication per shop instead: it is returned with the search results.')
    this.deprecatedStockAmountWarningAt = value
  }

  get stockAmountWarningAt(): number | undefined {
    return this.deprecatedStockAmountWarningAt
  }

  @property({ type: Number, attribute: 'display-limit' })
  displayLimit = 15

  /** @deprecated Use `display-limit` instead. */
  @property({ type: Number, attribute: 'number-of-products-per-page' })
  set numberOfProductsPerPage(value: number) {
    console.warn('<csc-variant-matrix> "number-of-products-per-page" is deprecated, use "display-limit" instead.')
    this.displayLimit = value
  }

  get numberOfProductsPerPage() {
    return this.displayLimit
  }

  @property({ type: String, attribute: 'sort-mode' })
  sortMode = 'code-asc'

  @state()
  private filters: VariantMatrixFilter[] = []

  @state()
  private products: VariantMatrixProduct[] = []

  @state()
  private selectedFilters: VariantMatrixSelectedFilter[] = []

  @state()
  private sortModes: VariantMatrixSortMode[] = []

  @state()
  private paginator: VariantMatrixPaginator | null = null

  @state()
  private totalFound = 0

  @state()
  private loading = true

  @state()
  private tableLoading = false

  @state()
  private loadingMore = false

  @state()
  private hiddenColumnKeys: string[] = []

  @state()
  private lastPageSize = 0

  @state()
  private stockOnly = false

  @state()
  private totalFoundWithoutStock = 0

  @query('csc-variant-matrix-filter-slide-in')
  private filterSlideIn?: CSCVariantMatrixFilterSlideIn

  @query('csc-variant-matrix-column-slide-in')
  private columnSlideIn?: CSCVariantMatrixColumnSlideIn

  private _matrixService?: VariantMatrixServiceContract

  private getService(): VariantMatrixServiceContract {
    if (!this._matrixService) {
      this._matrixService = new VariantMatrixService(
        this.productApi,
        this.matrixApi,
        this.variantGroupId,
        this.filterAttributes,
        this.displayLimit,
        this.sortMode,
      )
    }
    return this._matrixService
  }

  public set matrixService(service: VariantMatrixServiceContract) {
    this._matrixService = service
  }

  override async connectedCallback() {
    super.connectedCallback()
    if (this.variantGroupId || this._matrixService) {
      await this.loadInitialResults()
      return
    }

    createEffect(() => {
      const product = this.pdpProductStore.product()
      if (product && !this.pdpProductResolved) {
        this.pdpProductResolved = true
        void this.loadPdpProductResults(product)
      }
    })
  }

  private async loadPdpProductResults(product: PdpProduct) {
    if (!product.variant || (product.num_variants ?? 0) <= 1) {
      this.pdpProductWithoutVariants = true
      this.loading = false
      return
    }

    this.variantGroupId = product.variant.id
    if (!this.filterAttributes.length) {
      this.filterAttributes = product.variant.attributes.map(attribute => attribute.id)
    }
    await this.loadInitialResults()
  }

  private async loadInitialResults() {
    this.loading = true
    try {
      const results = await this.getService().getInitialSearchResults()
      await this.applySearchResults(results)
    } catch (error) {
      console.error('Retrieving the variant matrix failed', error)
    } finally {
      this.loading = false
    }
  }

  private async applySearchResults(results: {
    attributes: VariantMatrixFilter[]
    products: VariantMatrixProduct[]
    selected_filters: VariantMatrixSelectedFilter[]
    sort_modes: VariantMatrixSortMode[]
    paginator: VariantMatrixPaginator
    total_found: number
  }) {
    const filteredProducts = this.dedupeProducts(this.filterProductsByStock([...results.products]))

    this.filters = [...results.attributes]
    this.products = filteredProducts
    this.selectedFilters = [...results.selected_filters]
    this.sortModes = [...results.sort_modes]
    this.paginator = { ...results.paginator }
    this.totalFound = await this.resolveTotalFound(results, filteredProducts)
    this.lastPageSize = results.products.length
    this.syncColumnVisibility()
    this.dispatchEvent(new PlpFiltersSelectedFiltersEvent(this.displaySelectedFilters, ''))
  }

  private async resolveTotalFound(
    results: {
      products: VariantMatrixProduct[]
      paginator: VariantMatrixPaginator
      total_found: number
    },
    filteredProducts: VariantMatrixProduct[],
  ): Promise<number> {
    if (!this.stockOnly) {
      this.totalFoundWithoutStock = results.total_found
      return results.total_found
    }

    if (!results.paginator.has_next) {
      return filteredProducts.length
    }

    if (
      this.totalFoundWithoutStock > 0
      && results.total_found < this.totalFoundWithoutStock
    ) {
      return results.total_found
    }

    return this.countInStockAcrossPages(results)
  }

  private async countInStockAcrossPages(results: {
    products: VariantMatrixProduct[]
    paginator: VariantMatrixPaginator
  }): Promise<number> {
    let count = this.filterProductsByStock(results.products).length
    let nextPageUrl: string | undefined = results.paginator.next_page_api_link

    while (nextPageUrl) {
      const pageResults = await this.getService().getSearchResults(nextPageUrl, {
        isPagination: true,
        inStock: true,
      })
      count += this.filterProductsByStock(pageResults.products).length
      nextPageUrl = pageResults.paginator.has_next
        ? pageResults.paginator.next_page_api_link
        : undefined
    }

    return count
  }

  private get displaySelectedFilters(): VariantMatrixSelectedFilter[] {
    if (!this.stockOnly) return this.selectedFilters
    return [...this.selectedFilters, createStockSelectedFilter()]
  }

  private isProductInStock(product: VariantMatrixProduct): boolean {
    const indication = product.stock.indication
    if (indication?.style) {
      return indication.style !== 'error'
    }
    return (product.stock.amount ?? 0) > 0
  }

  private filterProductsByStock(products: VariantMatrixProduct[]): VariantMatrixProduct[] {
    if (!this.stockOnly) return products
    return products.filter(product => this.isProductInStock(product))
  }

  private syncColumnVisibility() {
    const validKeys = new Set(this.filters.map(filter => `filter_${filter.id}`))
    this.hiddenColumnKeys = this.hiddenColumnKeys.filter(key => validKeys.has(key))
  }

  private findSelectedFilterLink(
    filter: VariantMatrixFilter,
    option: VariantMatrixFilterOption,
    selectedFilters: VariantMatrixSelectedFilter[],
  ): string | undefined {
    const label = `${filter.name} - ${option.name}`
    const selected = selectedFilters.find(selectedFilter => (
      selectedFilter.name === label
      || selectedFilter.name === option.name
      || (selectedFilter.name.startsWith(`${filter.name}`) && selectedFilter.name.includes(option.name))
    ))
    return selected?.api_link
  }

  private async onFiltersApply(event: VariantMatrixFiltersApplyEvent) {
    const stockChanged = event.stockOnly !== this.stockOnly
    if (!event.changes.length && !stockChanged) return

    this.filterSlideIn?.close()
    this.tableLoading = true

    try {
      this.stockOnly = event.stockOnly
      this.getService().setStockOnly(this.stockOnly)

      let currentFilters = this.filters
      let currentSelectedFilters = this.selectedFilters
      let results: Awaited<ReturnType<VariantMatrixService['getSearchResults']>> | null = null

      for (const change of event.changes) {
        const filter = currentFilters.find(item => item.id === change.filterId)
        if (!filter) continue

        const options = filter.options ?? filter.values ?? []
        const option = options.find(item => item.id === change.optionId)
        if (!option) continue

        const url = change.selected
          ? option.value
          : this.findSelectedFilterLink(filter, option, currentSelectedFilters)

        if (!url) continue

        results = await this.getService().getSearchResults(url, { inStock: this.stockOnly })
        currentFilters = results.attributes
        currentSelectedFilters = results.selected_filters
      }

      if (!results && stockChanged) {
        results = await this.getService().refreshCurrentResults()
      }

      if (results) {
        await this.applySearchResults(results)
        this.filterSlideIn?.syncAppliedState(this.filters, this.stockOnly)
      }
    } finally {
      this.tableLoading = false
    }
  }

  private async clearFilters() {
    this.stockOnly = false
    this.getService().setStockOnly(false)
    await this.loadInitialResults()
  }

  private async onFilterRemoved(event: FilterRemovedEvent) {
    if (isStockSelectedFilter(event.filter)) {
      this.stockOnly = false
      this.getService().setStockOnly(false)
      const results = await this.getService().refreshCurrentResults()
      await this.applySearchResults(results)
      return
    }

    if (!event.filter.api_link) return
    const results = await this.getService().getSearchResults(event.filter.api_link, { inStock: this.stockOnly })
    await this.applySearchResults(results)
  }

  private async onFiltersCleared() {
    await this.clearFilters()
    this.filterSlideIn?.close()
  }

  private onColumnsApplied(event: VariantMatrixColumnsAppliedEvent) {
    const visibleKeys = new Set(event.visibleColumnKeys)
    this.hiddenColumnKeys = this.filters
      .map(filter => `filter_${filter.id}`)
      .filter(key => !visibleKeys.has(key))
  }

  private async onSortModeSelected(mode: VariantMatrixSortMode) {
    if (!mode.api_link || mode.selected) return
    const results = await this.getService().getSearchResults(mode.api_link, { inStock: this.stockOnly })
    await this.applySearchResults(results)
  }

  private openFilterSlideIn() {
    this.filterSlideIn?.open()
  }

  private openColumnSlideIn() {
    this.columnSlideIn?.open()
  }

  private dedupeProducts(products: VariantMatrixProduct[]): VariantMatrixProduct[] {
    const seen = new Set<number>()
    return products.filter(product => {
      if (seen.has(product.id)) return false
      seen.add(product.id)
      return true
    })
  }

  private async loadMore() {
    if (this.loadingMore || !this.hasMoreVariants) return

    let nextPageUrl = this.paginator?.next_page_api_link
    if (!nextPageUrl) return

    this.loadingMore = true

    try {
      let lastResults: Awaited<ReturnType<VariantMatrixService['getSearchResults']>> | null = null
      const newProducts: VariantMatrixProduct[] = []

      while (nextPageUrl) {
        const results = await this.getService().getSearchResults(nextPageUrl, {
          isPagination: true,
          inStock: this.stockOnly,
        })
        lastResults = results

        const existingIds = new Set([
          ...this.products.map(product => product.id),
          ...newProducts.map(product => product.id),
        ])
        const pageProducts = this.filterProductsByStock(results.products)
          .filter(product => !existingIds.has(product.id))

        newProducts.push(...pageProducts)

        const shouldFetchAnotherPage = this.stockOnly
          && pageProducts.length === 0
          && results.paginator.has_next
          && results.paginator.next_page_api_link

        if (!shouldFetchAnotherPage) {
          break
        }

        nextPageUrl = results.paginator.next_page_api_link
      }

      if (!lastResults) return

      const filteredProducts = this.dedupeProducts(this.filterProductsByStock([
        ...this.products,
        ...newProducts,
      ]))

      this.filters = [...lastResults.attributes]
      this.products = filteredProducts
      this.selectedFilters = [...lastResults.selected_filters]
      this.sortModes = [...lastResults.sort_modes]
      this.paginator = { ...lastResults.paginator }
      if (!this.stockOnly) {
        this.totalFound = lastResults.total_found
      } else if (!lastResults.paginator.has_next) {
        this.totalFound = filteredProducts.length
      }
      this.lastPageSize = lastResults.products.length
      this.dispatchEvent(new PlpFiltersSelectedFiltersEvent(this.displaySelectedFilters, ''))
    } finally {
      this.loadingMore = false
    }
  }

  private get shownVariantsCount(): number {
    return Math.min(this.products.length, this.totalFound)
  }

  private get hasMoreVariants(): boolean {
    return Boolean(
      this.paginator?.has_next
      && this.paginator?.next_page_api_link
      && this.products.length < this.totalFound,
    )
  }

  private get loadMoreCount(): number {
    const pageSize = this.lastPageSize || this.displayLimit
    return Math.min(pageSize, Math.max(this.totalFound - this.products.length, 0))
  }

  private getOrderTemplateQuantity(product: VariantMatrixProduct) {
    return product.ordering.minimum_order_quantity ?? product.ordering.order_quantity ?? 1
  }

  private hasPriceData(product: VariantMatrixProduct): boolean {
    return product.prices != null && product.prices.sale != null && product.prices.sale !== ''
  }

  private get showPrices(): boolean {
    return this.displayPrices && this.products.some(product => this.hasPriceData(product))
  }

  private formatPrice(product: VariantMatrixProduct): string {
    const sale = product.prices?.sale ?? ''
    const amount = Number(sale)
    if (Number.isNaN(amount)) return sale
    try {
      return Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: product.prices?.currency_code || 'EUR',
      }).format(amount)
    } catch {
      return sale
    }
  }

  private getAttributeValue(product: VariantMatrixProduct, filterName: string): string {
    return product.variant.attributes.find(attribute => attribute.name === filterName)?.value ?? ''
  }

  private get columnOptions(): VariantMatrixColumnOption[] {
    return this.filters.map(filter => ({
      key: `filter_${filter.id}`,
      name: filter.name,
      visible: !this.hiddenColumnKeys.includes(`filter_${filter.id}`),
    }))
  }

  private get tableRows(): VariantMatrixTableRow[] {
    return this.products.map(product => {
      const row: VariantMatrixTableRow = {
        product,
        price: product,
        order: product,
      }

      for (const filter of this.filters) {
        if (this.hiddenColumnKeys.includes(`filter_${filter.id}`)) continue
        row[`filter_${filter.id}`] = this.getAttributeValue(product, filter.name)
      }

      return row
    })
  }

  private get tableColumns(): VariantMatrixTableColumn[] {
    const columns: VariantMatrixTableColumn[] = [
      {
        name: String(translations.product()),
        key: 'product',
        width: 'var(--csc-variant-matrix-product-column-width, var(--vm-product-column-width, var(--csc-variant-matrix-default-product-column-width, 285px)))',
        truncate: true,
        cellRenderer: this.productCellRenderer,
      },
      ...this.filters
        .filter(filter => !this.hiddenColumnKeys.includes(`filter_${filter.id}`))
        .map(filter => ({
          name: filter.name,
          key: `filter_${filter.id}`,
          width: 'minmax(var(--csc-variant-matrix-attribute-column-min-width, var(--vm-attribute-column-min-width, 140px)), var(--csc-variant-matrix-attribute-column-max-width, var(--vm-attribute-column-max-width, 220px)))',
          truncate: true,
        })),
    ]

    if (this.showPrices) {
      columns.push({
        name: String(translations.price()),
        key: 'price',
        width: 'minmax(120px, auto)',
        align: 'right',
        truncate: true,
        cellRenderer: this.priceCellRenderer,
      })
    }

    if (this.canOrder || this.canAddToOrderTemplate) {
      columns.push({
        name: '',
        key: 'order',
        width: this.showPrices ? 'min-content' : 'minmax(120px, auto)',
        cellRenderer: this.orderCellRenderer,
      })
    }

    return columns
  }

  private get visibleFilters(): VariantMatrixFilter[] {
    return this.filters.filter(filter => !this.hiddenColumnKeys.includes(`filter_${filter.id}`))
  }

  private renderStockIndication(product: VariantMatrixProduct) {
    if (!this.displayStock) return nothing

    const indication = product.stock.indication

    return html`
      <csc-stock-indication
        variant="simple"
        stock="${String(product.stock.amount ?? 0)}"
        warning-amount="${ifDefined(this.deprecatedStockAmountWarningAt)}"
        indication-style="${ifDefined(indication?.style)}"
        indication-icon="${ifDefined(indication?.icon)}"
        indication-text="${ifDefined(indication?.text)}"
        tooltip-text="${ifDefined(indication?.tooltip ?? undefined)}"
      ></csc-stock-indication>
    `
  }

  private productCellRenderer = (value: unknown) => {
    const product = value as VariantMatrixProduct

    return html`
      <csc-product-preview
        size="small"
        stock-indication-variant="simple"
        image-source="${product.picture.mini}"
        product-name="${product.name}"
        page-link="${product.page_link}"
        product-name-clamp="2"
        product-id="${product.code}"
        stock="${this.displayStock ? String(product.stock.amount ?? 0) : ''}"
        stock-warning-amount="${ifDefined(this.displayStock ? this.deprecatedStockAmountWarningAt : undefined)}"
        stock-indication-style="${ifDefined(this.displayStock ? product.stock.indication?.style : undefined)}"
        stock-indication-icon="${ifDefined(this.displayStock ? product.stock.indication?.icon : undefined)}"
        stock-indication-text="${ifDefined(this.displayStock ? product.stock.indication?.text : undefined)}"
        stock-tooltip="${ifDefined(this.displayStock ? product.stock.indication?.tooltip ?? undefined : undefined)}"
      ></csc-product-preview>
    `
  }

  private priceCellRenderer = (value: unknown) => {
    const product = value as VariantMatrixProduct
    if (!this.hasPriceData(product)) return nothing

    return html`
      <cse-price size="m" layout="inline" uom="${product.ordering.default_uom}">
        ${this.formatPrice(product)}
      </cse-price>
      ${product.prices?.price_per_price_uom ? html`
        <cse-text variant="caption-m">${product.prices?.price_per_price_uom} / ${product.prices?.price_uom ?? product.ordering.default_uom}</cse-text>
      ` : nothing}
    `
  }

  private getOrderStep(product: VariantMatrixProduct): number {
    const step = product.ordering.order_quantity_dec ?? product.ordering.order_quantity ?? 1
    const parsed = Number(step)
    return Number.isNaN(parsed) ? 1 : parsed
  }

  private getOrderDefault(product: VariantMatrixProduct): number | undefined {
    const value = product.ordering.default_order_quantity_dec ?? product.ordering.default_order_quantity
    if (value == null) return undefined
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  private renderOrderActions(product: VariantMatrixProduct, layout: 'table' | 'mobile' = 'table') {
    const cannotBeAddedToBasket = Boolean(
      product.ordering.cannot_be_added_to_basket || product.status.can_order_product === false,
    )
    const isMobile = layout === 'mobile'
    const actionsClass = isMobile ? 'variant-matrix__mobile-order-actions' : 'cell-actions'
    const formClass = isMobile ? 'variant-matrix__mobile-order-form' : 'cell-actions__primary'
    const orderTemplateClass = isMobile ? '' : 'cell-actions__secondary'

    return html`
      <div class="${actionsClass}">
        ${this.canOrder ? html`
          <csc-pdp-provider>
            <csc-order-form
              class="${formClass}"
              product-id="${product.id}"
              min="${ifDefined(product.ordering.minimum_order_quantity)}"
              max="${ifDefined(product.ordering.maximum_order_quantity)}"
              step="${this.getOrderStep(product)}"
              default="${ifDefined(this.getOrderDefault(product))}"
              ?cannot-be-added-to-basket="${cannotBeAddedToBasket}"
              @click="${(event: Event) => event.stopPropagation()}"
            >
              ${this.displayUom && product.ordering.default_uom ? html`
                <cse-text variant="caption-m" slot="uom">${product.ordering.default_uom}</cse-text>
              ` : nothing}
            </csc-order-form>
          </csc-pdp-provider>
        ` : nothing}

        ${this.canAddToOrderTemplate ? html`
          <csc-order-template-slide-in
            class="${orderTemplateClass}"
            product-id="${product.id}"
            quantity="${this.getOrderTemplateQuantity(product)}"
            trigger-type="icon-button"
            trigger-variant="secondary"
            trigger-size="default"
          ></csc-order-template-slide-in>
        ` : nothing}
      </div>
    `
  }

  private orderCellRenderer = (value: unknown) => {
    return this.renderOrderActions(value as VariantMatrixProduct, 'table')
  }

  private renderMobilePrice(product: VariantMatrixProduct) {
    if (!this.showPrices || !this.hasPriceData(product)) return nothing

    return html`
      <div class="variant-matrix__mobile-price">
        <cse-price size="m" layout="inline" uom="${product.ordering.default_uom}">
          ${this.formatPrice(product)}
        </cse-price>
        ${product.prices?.price_per_price_uom ? html`
          <cse-text variant="caption-m">
            ${product.prices?.price_per_price_uom} / ${product.prices?.price_uom ?? product.ordering.default_uom}
          </cse-text>
        ` : nothing}
      </div>
    `
  }

  private renderMobileProductCard(product: VariantMatrixProduct) {
    return html`
      <cse-paper variant="primary" bordered class="variant-matrix__mobile-card">
        <div class="variant-matrix__mobile-card-header">
          <cse-image
            source="${product.picture.mini}"
            alt="${product.name}"
            width="50"
            height="50"
          ></cse-image>
          <div class="variant-matrix__mobile-card-info">
            <cse-text variant="product-title-m" clamp="2">${product.name}</cse-text>
            <cse-text variant="caption-l">${product.code}</cse-text>
            ${this.renderStockIndication(product)}
          </div>
        </div>

        ${this.visibleFilters.length ? html`
          <div class="variant-matrix__mobile-card-attributes">
            ${this.visibleFilters.map(filter => html`
              <div class="variant-matrix__mobile-attribute">
                <cse-text variant="caption-m" class="variant-matrix__mobile-attribute-label">
                  ${filter.name}
                </cse-text>
                <cse-text variant="body-s" class="variant-matrix__mobile-attribute-value">
                  ${this.getAttributeValue(product, filter.name)}
                </cse-text>
              </div>
            `)}
          </div>
        ` : nothing}

        ${this.renderMobilePrice(product)}

        ${this.canOrder || this.canAddToOrderTemplate
          ? this.renderOrderActions(product, 'mobile')
          : nothing}
      </cse-paper>
    `
  }

  private renderMobileProductList() {
    return html`
      <div class="variant-matrix__mobile-list">
        ${this.products.map(product => this.renderMobileProductCard(product))}
      </div>
    `
  }

  private get selectedSortModeName(): string {
    const selected = this.sortModes.find(mode => mode.selected)
    return selected?.name ?? String(t('sort_mode', { ns: 'csc-variant-matrix', default: 'Sort mode' }))
  }

  private get activeFilterCount(): number {
    return this.displaySelectedFilters.length
  }

  private renderTableToolbar() {
    const badgeCount = this.activeFilterCount

    return html`
      <div class="variant-matrix__toolbar">
        ${this.sortModes.length ? html`
          <cse-dropdown class="variant-matrix__sort" alignment="right" menu-width="anchor">
            ${this.sortModes.map(mode => html`
              <cse-option
                value="${mode.api_link}"
                ?selected=${mode.selected}
                @click=${() => this.onSortModeSelected(mode)}
              >
                ${mode.name}
              </cse-option>
            `)}
            <cse-chip slot="anchor" variant="dropdown">
              <cse-icon icon-name="sort" icon-style="solid" slot="prefix"></cse-icon>
              ${this.selectedSortModeName}
            </cse-chip>
          </cse-dropdown>
        ` : nothing}

        <div class="variant-matrix__toolbar-filter-wrap">
          <cse-chip class="variant-matrix__toolbar-filter" @click=${this.openFilterSlideIn}>
            <cse-icon icon-name="sliders" icon-style="solid" slot="prefix"></cse-icon>
            ${t('filter', { ns: 'csc-variant-matrix', default: 'Filter' })}
          </cse-chip>
          ${badgeCount > 0 ? html`
            <cse-badge
              class="variant-matrix__toolbar-filter-badge"
              .size=${'small' as const}
              .composition=${'floating' as const}
              .variant=${'error' as const}
            >${badgeCount}</cse-badge>
          ` : nothing}
        </div>

        <cse-chip class="variant-matrix__toolbar-columns" @click=${this.openColumnSlideIn}>
          <cse-icon icon-name="pen-to-square" icon-style="solid" slot="prefix"></cse-icon>
          ${t('edit_columns', { ns: 'csc-variant-matrix', default: 'Edit columns' })}
        </cse-chip>
      </div>
    `
  }

  override render() {
    if (this.pdpProductWithoutVariants) return nothing

    if (this.loading) {
      return html`<cse-skeleton height="320" variant="secondary"></cse-skeleton>`
    }

    return html`
      <div class="variant-matrix">
        <cse-text class="variant-matrix__heading" variant="heading-3" as="h3">
          ${translations.allVariants()}
        </cse-text>

        <div class="variant-matrix__content">
          <div class="variant-matrix__panel-wrapper">
            ${this.renderTableToolbar()}

            <div class=${classMap({ 'variant-matrix__panel': true, 'variant-matrix__panel--empty': !this.displaySelectedFilters.length })}>
              ${this.displaySelectedFilters.length ? html`
                <div class="variant-matrix__active-filters-container">
                  <csc-active-filters
                    class="variant-matrix__active-filters"
                    hidden-fields="filter-name"
                    .filters=${this.displaySelectedFilters}
                    @filter-removed=${this.onFilterRemoved}
                    @filters-cleared=${this.onFiltersCleared}
                  ></csc-active-filters>
                </div>
              ` : nothing}

              <div class="variant-matrix__table">
                ${this.tableLoading
                  ? html`<cse-skeleton height="320" variant="secondary"></cse-skeleton>`
                  : html`
                    <csc-table
                      variant="line"
                      .columns="${this.tableColumns}"
                      .data="${this.tableRows}"
                    ></csc-table>
                  `}
              </div>
            </div>
          </div>

          ${this.renderMobileProductList()}
        </div>

        <div class="variant-matrix__footer">
          <cse-text variant="caption-m">
            ${t('showing_variants', {
              ns: 'csc-variant-matrix',
              default: 'Showing {{variable_shown}} of {{variable_total}} variants',
              variable_shown: this.shownVariantsCount,
              variable_total: this.totalFound,
            })}
          </cse-text>

          ${this.hasMoreVariants ? html`
            <cse-chip @click=${this.loadMore} ?disabled=${this.loadingMore}>
              <cse-icon icon-name="refresh" icon-style="solid" slot="prefix"></cse-icon>
              ${t('load_more_count', {
                ns: 'csc-variant-matrix',
                default: 'Load {{variable_count}} more',
                variable_count: this.loadMoreCount,
              })}
            </cse-chip>
          ` : nothing}
        </div>

        <csc-variant-matrix-filter-slide-in
          .filters=${this.filters}
          .selectedFilters=${this.displaySelectedFilters}
          @variant-matrix-filters-apply=${this.onFiltersApply}
        ></csc-variant-matrix-filter-slide-in>
        <csc-variant-matrix-column-slide-in
          .columns=${this.columnOptions}
          @variant-matrix-columns-applied=${this.onColumnsApplied}
        ></csc-variant-matrix-column-slide-in>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-variant-matrix': CSCVariantMatrix
  }
}
